import numeral from 'numeral';
import Modal from '../../Common/Modal';
import Label from '../../Common/Label';
import InputText from '../../Common/InputText';
import Button from '../../Common/Button';
import { useFormik } from 'formik';
import { Booking, CreditCard } from '@deliveries-app/models';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { bookingService } from '@deliveries-app/services';
import toast from 'react-hot-toast';
import { countTotalPrice, darkenColor } from '../../../utils/helpers';

interface PaymentProps {
  open: boolean;
  onClose: () => void;
  onSubmit: ({
    invoice_number,
    total_price,
  }: {
    invoice_number: string;
    total_price: number;
  }) => void;
  booking: Booking;
}

const PaymentModal = ({ open, onClose, booking, onSubmit }: PaymentProps) => {
  const { values, handleSubmit, handleChange, resetForm, errors } = useFormik({
    enableReinitialize: true,
    initialValues: {
      invoice_number: booking.financial ? booking.financial.invoice_number : '',
      total_price: booking.financial ? booking.financial.total_price : 0,
    },
    validate: (values) => {
      const err: { invoice_number?: string; total_price?: string } = {};
      const total = Number(values.total_price);
      const hasInvoice = String(values.invoice_number ?? '').trim().length > 0;
      if (total > 0 && !hasInvoice) {
        err.invoice_number = 'Required when total amount is filled.';
      }
      if (
        hasInvoice &&
        total <= 0 &&
        booking.non_chargeable === false
      ) {
        err.total_price = 'Required when invoice number is filled.';
      }
      return err;
    },
    onSubmit: (values) => {
      // dispatch(
      //   createTodo(branch_id, moment(date).format('YYYY-MM-DD'), values.content)
      // );
      // formik.resetForm();
      onSubmit(values);
    },
  });


  const invoiceRequired = Number(values.total_price) > 0;

  const onCancel = () => {
    resetForm();
    onClose();
  };
  const [card, setCard] = useState<CreditCard>();
  const [showCard, setShowCard] = useState(false);

  const fetchCreditCard = async () => {
    const toastId = toast.loading('Fetching...');
    try {
      const card = await bookingService.accessCreditCardData(
        booking.branch.id,
        booking.id
      );

      setCard(card);
      setShowCard(true);

      toast.success('Card fetched!', { id: toastId });
    } catch (error) {
      console.log(error);
      toast.error('Error fetching card!', { id: toastId });
    }
  };

  return (
    <Modal open={open} closeModal={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="mb-6 space-y-2">
          <h1 className="text-xl font-semibold text-gray-900">Credit Card</h1>
          {!showCard ? (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={fetchCreditCard}
                className={`rounded p-0.5 hover:bg-gray-100 focus:outline-none focus:ring-2`}
                style={{
                  transition: 'background-color 0.3s, box-shadow 0.3s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    darkenColor(booking.branch.branch_colour, 20);
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    'white';
                }}
                onFocus={(e) => {
                  (
                    e.currentTarget as HTMLButtonElement
                  ).style.boxShadow = `0 0 0 3px ${booking.branch.branch_colour}`;
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    'none';
                }}
              >
                <span className="sr-only">View credit card</span>
                <EyeIcon
                  className={`h-6`}
                  style={{
                    color: booking.branch.branch_colour,
                  }}
                />
              </button>
              <span className="block blur">4242 4242 4242 4242 4/24</span>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setShowCard(false)}
                className={`rounded p-0.5 hover:bg-gray-100 focus:outline-none focus:ring-2`}
                style={{
                  transition: 'background-color 0.3s, box-shadow 0.3s',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    darkenColor(booking.branch.branch_colour, 20);
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    'white';
                }}
                onFocus={(e) => {
                  (
                    e.currentTarget as HTMLButtonElement
                  ).style.boxShadow = `0 0 0 3px ${booking.branch.branch_colour}`;
                }}
                onBlur={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.boxShadow =
                    'none';
                }}
              >
                <span className="sr-only">Hide credit card</span>
                <EyeSlashIcon
                  className={`h-6`}
                  style={{
                    color: booking.branch.branch_colour,
                  }}
                />
              </button>
              <span className="block">
                {!card
                  ? 'No saved credit card, edit booking to add'
                  : card.number +
                    '   exp: ' +
                    card.expiration.split('-')[1] +
                    '/' +
                    card.expiration.split('-')[0]}
              </span>
            </div>
          )}
        </div>

        <div className="mb-6 space-y-4">
          <h1 className="text-xl font-semibold text-gray-900">
            Booking Information
          </h1>
          <div className="w-1/2">
            <div className="flex flex-1">
              <div className="w-1/2">Delivery Fee</div>
              <div className="w-1/2">
                {numeral(booking.financial.delivery_fee).format('$0,0.00')}
              </div>
            </div>
            <div className="flex flex-1">
              <div className="w-1/2">Product Price</div>
              <div className="w-1/2">
                {numeral(
                  countTotalPrice(
                    booking.booking_line,
                    booking.financial.delivery_fee
                  ).totalPrice
                ).format('$0,0.00')}
              </div>
            </div>
            <div className="flex flex-1 font-bold">
              <div className="w-1/2">Total Price</div>
              <div className="w-1/2">
                {numeral(
                  countTotalPrice(
                    booking.booking_line,
                    booking.financial.delivery_fee
                  ).totalAmount
                ).format('$0,0.00')}
              </div>
            </div>
            {/* <div className="flex flex-1">
              <div className="w-1/2">Delivery Cost</div>
              <div className="w-1/2">
                {numeral(booking.financial.delivery_cost).format('$0,0.00')}
              </div>
            </div> */}
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <Label name="Invoice Number" optional={!invoiceRequired} />
              <div className="mt-1">
                <InputText
                  branchColour={booking.branch.branch_colour}
                  type={'text'}
                  name="invoice_number"
                  id="invoice_number"
                  value={values.invoice_number}
                  onChange={handleChange}
                  isRequired={invoiceRequired}
                  required={invoiceRequired}
                />
                {errors.invoice_number && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.invoice_number}
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label name="Total Amount" optional={false} />
              <div className="mt-1">
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-gray-500"> $ </span>
                  <InputText
                    branchColour={booking.branch.branch_colour}
                    type={'number'}
                    id="total_price"
                    name="total_price"
                    value={values.total_price}
                    onChange={handleChange}
                    step=".01"
                    className="pl-8"
                  />
                </div>
                {errors.total_price && (
                  <div className="mt-1 text-sm text-red-500">
                    {errors.total_price}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-4">
          <Button onClick={() => onCancel()} type="button">
            Close
          </Button>
          <Button type="submit">Charge</Button>
        </div>
      </form>
    </Modal>
  );
};

export default PaymentModal;
